import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#maybeHydrateEmbeddedCollectionFacets();
  }

  /**
   * Homepage (and similar) featured collections render without collection URL context,
   * so storefront filters are empty in SSR HTML. Re-fetch the section once using the
   * collection path so filter accordions and filter drawer populate.
   */
  #maybeHydrateEmbeddedCollectionFacets() {
    if (this.dataset.facetsContextHydrated === 'true') return;

    /** @type {Window & {Shopify?: { designMode?: boolean }}} */
    const win = window;
    if (win.Shopify?.designMode) {
      this.dataset.facetsContextHydrated = 'true';
      return;
    }

    const base = this.getAttribute('data-section-render-base-url');
    if (!base) {
      this.dataset.facetsContextHydrated = 'true';
      return;
    }

    const sectionId = this.getAttribute('section-id');
    if (!sectionId) {
      this.dataset.facetsContextHydrated = 'true';
      return;
    }

    const desktopForm = document.getElementById(`FacetFiltersForm--${sectionId}-desktop`);
    const hasFacetItems = desktopForm?.querySelector('.facets__item');

    if (hasFacetItems) {
      this.dataset.facetsContextHydrated = 'true';
      return;
    }

    requestIdleCallback(() => {
      const url = new URL(base, window.location.origin);
      const current = new URL(window.location.href);
      for (const [key, value] of current.searchParams) {
        if (key === 'section_id') continue;
        url.searchParams.append(key, value);
      }

      sectionRenderer
        .renderSection(sectionId, { url, cache: false })
        .catch(() => {})
        .finally(() => {
          this.dataset.facetsContextHydrated = 'true';
        });
    });
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
